// MaturaParityDigitsH00ClosedTask.js
// Zadanie maturalne – zamknięte (ABCD)
// Trzycyfrowe liczby > H00 lub < H00, w zapisie tylko cyfry spełniające
// warunek z treści (parzyste/nieparzyste, mniejsze od x, większe od x).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const randInt = (a, b) =>
  Math.floor(Math.random() * (b - a + 1)) + a;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function digitsToSetLatex(digs) {
  return `\\{${digs.join(",")}\\}`;
}

// policzenie ile jest liczb spełniających warunek,
// dla ogólnego zbioru cyfr D (cyfry dziesiątek/jedności) i H (setki)
function countNumbers(H, relation, digitsD, digitsH) {
  const sizeD = digitsD.length;
  const sizeH = digitsH.length;

  if (sizeD === 0 || sizeH === 0) return 0;

  if (relation === "greater") {
    const greaterH = digitsH.filter((d) => d > H).length;
    const part1 = greaterH * sizeD * sizeD;

    let part2 = 0;
    if (digitsH.includes(H)) {
      // liczby z setkami = H
      if (digitsD.includes(0)) {
        // gdy 0 jest dozwolone, jedna liczba to H00 – trzeba ją odrzucić
        part2 = sizeD * sizeD - 1;
      } else {
        part2 = sizeD * sizeD;
      }
    }
    return part1 + part2;
  }

  // relation === "less"
  const lessH = digitsH.filter((d) => d < H).length;
  return lessH * sizeD * sizeD;
}

function createOriginalTask() {
  const digitsD = [1, 3, 5, 7, 9];
  const digitsH = digitsD;
  const answer = countNumbers(5, "greater", digitsD, digitsH);
  const options = [13, 50, 75, 107].map((value, index) => ({
    latex: `${value}`,
    value,
    isCorrect: value === answer,
    label: String.fromCharCode("A".charCodeAt(0) + index),
  }));
  const correctIndex = options.findIndex((option) => option.isCorrect);

  return {
    type: "MaturaParityDigitsH00ClosedTask",
    data: {
      H: 5,
      relation: "greater",
      conditionType: "parity",
      parity: "odd",
      x: null,
      conditionText: "tylko cyfry nieparzyste",
      digitsD,
      digitsH,
      answer,
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 28,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  while (true) {
    // H – w "H00"
    const H = randInt(3, 8);
    const relation = Math.random() < 0.5 ? "greater" : "less"; // > H00 lub < H00

    // typ warunku na cyfry
    // parity – parzyste/nieparzyste
    // less   – wszystkie cyfry < x
    // greater– wszystkie cyfry > x
    const conditionTypeRand = Math.random();
    let conditionType;
    if (conditionTypeRand < 1 / 3) conditionType = "parity";
    else if (conditionTypeRand < 2 / 3) conditionType = "less";
    else conditionType = "greater";

    const allDigits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

    let parity = null;
    let x = null;
    let digitsD;
    let digitsH;
    let conditionText; // tekst do treści

    if (conditionType === "parity") {
      parity = Math.random() < 0.5 ? "odd" : "even";
      if (parity === "odd") {
        digitsD = [1, 3, 5, 7, 9];
      } else {
        digitsD = [0, 2, 4, 6, 8];
      }
      digitsH = digitsD.filter((d) => d !== 0);

      conditionText =
        parity === "odd"
          ? "tylko cyfry nieparzyste"
          : "tylko cyfry parzyste";
    } else if (conditionType === "less") {
      x = randInt(3, 7);
      digitsD = allDigits.filter((d) => d < x);
      digitsH = digitsD.filter((d) => d !== 0);
      conditionText = `wszystkie cyfry są mniejsze od ${x}`;
    } else {
      // conditionType === "greater"
      x = randInt(3, 7);
      digitsD = allDigits.filter((d) => d > x);
      digitsH = digitsD.filter((d) => d !== 0);
      conditionText = `wszystkie cyfry są większe od ${x}`;
    }

    const answer = countNumbers(H, relation, digitsD, digitsH);

    // unikamy sytuacji z odpowiedzią 0
    if (!Number.isFinite(answer) || answer <= 0) {
      continue; // losujemy jeszcze raz całą konfigurację
    }

    // ---- GENERATOR DYSTRAKTORÓW ----

    const greaterH = digitsH.filter((d) => d > H).length;
    const lessH = digitsH.filter((d) => d < H).length;
    const hasH = digitsH.includes(H);
    const sizeD = digitsD.length;
    const sizeH = digitsH.length;

    // liczba wszystkich liczb trzycyfrowych z dozwolonymi cyframi (bez ograniczenia H00)
    const totalNoBound = sizeH * sizeD * sizeD;

    const candidateValues = [];

    const addCandidate = (v) => {
      if (!Number.isFinite(v)) return;
      if (v <= 0) return;
      if (v === answer) return;
      if (!candidateValues.includes(v)) candidateValues.push(v);
    };

    if (relation === "greater") {
      // błędny pomysł: bierzemy tylko setki > H (ignorujemy przypadek =H)
      const onlyGreater = greaterH * sizeD * sizeD;
      addCandidate(onlyGreater);

      // błędny pomysł: liczymy wszystkie liczby z danymi cyframi (bez „> H00”)
      addCandidate(totalNoBound);

      // błędny pomysł: setki >= H, bez odjęcia H00
      if (hasH) {
        const geH = (greaterH + 1) * sizeD * sizeD;
        addCandidate(geH);
      }
    } else {
      // relation === "less"
      // błędny pomysł: setki <= H (obejmują H00)
      if (hasH) {
        const leH = (lessH + 1) * sizeD * sizeD;
        addCandidate(leH);
      }

      // błędny pomysł: ignorujemy warunek H00
      addCandidate(totalNoBound);

      // „w okolicy” poprawnej odpowiedzi
      addCandidate(answer + 5);
      addCandidate(Math.max(1, answer - 5));
    }

    // dopasowanie wielkości dystraktorów
    let distractors = [...candidateValues];

    if (answer > 20) {
      const minReasonable = Math.max(3, Math.floor(answer / 3));
      distractors = distractors.filter((v) => v >= minReasonable);
    }

    // uzupełniamy, jeśli po filtracji jest ich za mało
    const nearSteps = [5, 10, 15];
    let stepIndex = 0;
    while (distractors.length < 3) {
      const step = nearSteps[stepIndex % nearSteps.length];
      const sign = stepIndex % 2 === 0 ? 1 : -1;
      const candidate = answer + sign * step;
      if (
        candidate > 0 &&
        candidate !== answer &&
        !distractors.includes(candidate)
      ) {
        distractors.push(candidate);
      }
      stepIndex += 1;
    }

    const rawNumbers = [answer, distractors[0], distractors[1], distractors[2]];
    const rawOptions = rawNumbers.map((v) => ({
      latex: `${v}`,
      value: v,
      isCorrect: v === answer,
    }));

    const shuffled = shuffle(rawOptions);
    const options = shuffled.map((opt, idx) => ({
      ...opt,
      label: String.fromCharCode("A".charCodeAt(0) + idx),
    }));
    const correctIndex = options.findIndex((o) => o.isCorrect);

    return {
      type: "MaturaParityDigitsH00ClosedTask",
      data: {
        H,
        relation,
        conditionType,
        parity,
        x,
        conditionText,
        digitsD,
        digitsH,
        answer,
        options,
        correctIndex,
      },
    };
  }
}

// ---------------- TREŚĆ ----------------

export function renderTask({ data }) {
  const { H, relation, conditionText, options } = data;

  const relText =
    relation === "greater" ? "większych od" : "mniejszych od";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>
        Wszystkich trzycyfrowych liczb naturalnych{" "}
        {relText} <InlineMath math={`${H}00`} />, w których zapisie
        dziesiętnym występują{" "}
        <strong>{conditionText}</strong>, jest:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- INPUT ABCD ----------------

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ---------------- SPRAWDZANIE ----------------

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// ---------------- POPRAWNA ODPOWIEDŹ ----------------

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// ---------------- ODPOWIEDŹ UŻYTKOWNIKA ----------------

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ---------------- WYJAŚNIENIE ----------------

// ---------------- WYJAŚNIENIE ----------------

export function renderExplanation({ data }) {
  const { H, relation, digitsD, digitsH, answer } = data;

  const setDLatex = digitsToSetLatex(digitsD);
  const setHLatex = digitsToSetLatex(digitsH);

  const sizeD = digitsD.length;
  const greaterH = digitsH.filter((d) => d > H).length;
  const lessH = digitsH.filter((d) => d < H).length;
  const hasH = digitsH.includes(H);

  let part1 = 0;
  let part2 = 0;

  const isGreater = relation === "greater";

  if (isGreater) {
    // wariant: "większych od H00"
    part1 = greaterH * sizeD * sizeD;

    if (hasH) {
      // drugi przypadek istnieje
      part2 = digitsD.includes(0)
        ? sizeD * sizeD - 1
        : sizeD * sizeD;
    }
  } else {
    // wariant: "mniejszych od H00"
    part1 = lessH * sizeD * sizeD;
    part2 = 0; // brak drugiego wariantu w "<"
  }

  const total = part1 + part2;
  const hasTwoCases = isGreater && hasH; // tylko wtedy rzeczywiście coś sumujemy

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      {/* --- KROK 1 --- */}
      <p>
        <strong>Krok 1. Zapisz zbiór dopuszczalnych cyfr.</strong>
      </p>
      <p>
        Cyfry, które spełniają warunek z treści, tworzą zbiór{" "}
        <InlineMath math={`D = ${setDLatex}`} />.
        <br />
        Na miejscu setek nie może stać <InlineMath math="0" />, więc cyfry setek pochodzą ze zbioru{" "}
        <InlineMath math={`H = ${setHLatex}`} />.
      </p>

      {/* --- KROK 2 --- */}
      <p>
        <strong>
          Krok 2. Użyj warunku „{isGreater ? "większych" : "mniejszych"} od{" "}
          <InlineMath math={`${H}00`} />”.
        </strong>
      </p>

      {isGreater ? (
        <>
          <p>
            Szukamy liczb większych od <InlineMath math={`${H}00`} />.
            Możliwe są cyfry setek większe niż{" "}
            <InlineMath math={`${H}`} /> oraz — jeśli{" "}
            <InlineMath math={`${H}`} /> należy do zbioru cyfr setek — cyfry
            równe <InlineMath math={`${H}`} />.
          </p>

          {hasTwoCases ? (
            <>
              {/* Przypadek 1 */}
              <p>
                <em>Przypadek <InlineMath math="1" />:</em> cyfra setek większa niż{" "}
                <InlineMath math={`${H}`} />.
              </p>
              <p>
                Takich cyfr jest{" "}
                <InlineMath math={`${greaterH}`} />, a dziesiątki i jedności
                mogą być dowolnymi cyframi z{" "}
                <InlineMath math={`D = ${setDLatex}`} />:
              </p>
              <BlockMath
                math={`${greaterH} \\cdot ${sizeD} \\cdot ${sizeD} = ${part1}`}
              />

              {/* Przypadek 2 */}
              <p>
                <em>Przypadek <InlineMath math="2" />:</em> cyfra setek równa{" "}
                <InlineMath math={`${H}`} />.
              </p>
              <p>
                Wtedy dziesiątki i jedności mogą być dowolnymi cyframi z{" "}
                <InlineMath math={`D = ${setDLatex}`} />, ale liczba nie może
                być równa <InlineMath math={`${H}00`} />:
              </p>
              <BlockMath
                math={
                  digitsD.includes(0)
                    ? `${sizeD}^{2} - 1 = ${part2}`
                    : `${sizeD}^{2} = ${part2}`
                }
              />
            </>
          ) : (
            // tylko jeden przypadek: setki > H
            <>
              <p>
                Cyfra <InlineMath math={`${H}`} /> nie należy do zbioru cyfr
                setek, więc jedyny przypadek to cyfra setek większa niż{" "}
                <InlineMath math={`${H}`} />.
              </p>
              <BlockMath
                math={`${greaterH} \\cdot ${sizeD} \\cdot ${sizeD} = ${part1}`}
              />
            </>
          )}
        </>
      ) : (
        // --- wariant "< H00" – zawsze jeden przypadek ---
        <>
          <p>
            Szukamy liczb mniejszych od <InlineMath math={`${H}00`} />.
            Jedyny możliwy przypadek to cyfra setek mniejsza niż{" "}
            <InlineMath math={`${H}`} />.
          </p>

          <p>
            Takich cyfr setek jest{" "}
            <InlineMath math={`${lessH}`} />, a dziesiątki i jedności mogą być
            dowolnymi cyframi z{" "}
            <InlineMath math={`D = ${setDLatex}`} />:
          </p>

          <BlockMath
            math={`${lessH} \\cdot ${sizeD} \\cdot ${sizeD} = ${part1}`}
          />
        </>
      )}

      {/* --- KROK 3 tylko gdy naprawdę coś sumujemy --- */}
      {hasTwoCases && (
        <>
          <p>
            <strong>Krok 3. Zsumuj przypadki.</strong>
          </p>
          <BlockMath math={`${part1} + ${part2} = ${total}`} />
        </>
      )}

      <p>
        Zatem takich liczb jest{" "}
        <InlineMath math={`${answer}`} />.
      </p>
    </div>
  );
}
